import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import CustomerNavigation from '../../components/CustomerNavigation';
import KakaoChatButton from '../../components/KakaoChatButton';
import SiteFooter from '../../components/SiteFooter';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Image from '../../components/AppImage';
import { supabase } from '../../lib/supabaseClient';

const PAGE_SIZE = 15;

const BeforeAfter = () => {
  const [page, setPage] = useState(1);
  const [posts, setPosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  }, [totalCount]);

  const fetchPosts = useCallback(async () => {
    setIsFetching(true);
    setFetchError('');

    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error, count } = await supabase
        .from('before_after_posts')
        .select('id,title,content,image_url,created_at', { count: 'exact' })
        .eq('is_published', true)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      setPosts(data || []);
      setTotalCount(count || 0);
    } catch (e) {
      setPosts([]);
      setTotalCount(0);
      setFetchError(e?.message || '비포&애프터 목록을 불러오지 못했습니다.');
    } finally {
      setIsFetching(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const isModalOpen = !!selectedPost;

  return (
    <>
      <Helmet>
        <title>비포&애프터 - SneakerCare Pro</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <CustomerNavigation />

        <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <div className="mb-4 inline-flex items-center justify-center rounded-full bg-primary/10 p-3">
              <Icon name="Image" size={32} color="var(--color-primary)" />
            </div>
            <h1 className="mb-3 text-3xl font-bold text-foreground sm:text-4xl">비포&애프터</h1>
            <p className="mx-auto max-w-2xl text-muted-foreground text-white">
              실제 케어 전후 변화를 확인해보세요.
            </p>
          </div>

          {isFetching ? (
            <div className="rounded-lg bg-card p-12 text-center shadow-card">
              <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              <p className="text-sm text-muted-foreground">목록을 불러오는 중...</p>
            </div>
          ) : fetchError ? (
            <div className="rounded-lg bg-card p-6 shadow-card">
              <div className="rounded-lg bg-error/10 p-4">
                <p className="text-sm text-error">{fetchError}</p>
              </div>
            </div>
          ) : posts?.length === 0 ? (
            <div className="rounded-lg bg-card p-12 text-center shadow-card">
              <Icon name="Image" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-bold text-foreground">등록된 게시물이 없습니다</h3>
              <p className="text-sm text-muted-foreground">조금만 기다려주세요.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6">
                {posts?.map((post) => (
                  <article
                    key={post?.id}
                    className="overflow-hidden rounded-xl bg-card shadow-card transition-all duration-300 hover:shadow-xl"
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedPost(post)}
                    onKeyDown={(e) => {
                      if (e?.key === 'Enter' || e?.key === ' ') {
                        e?.preventDefault();
                        setSelectedPost(post);
                      }
                    }}
                  >
                    <div className="relative">
                      <div className="aspect-[7/10] w-full overflow-hidden bg-muted">
                        <Image
                          src={post?.image_url}
                          alt={'Before & After'}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              <div className="mt-6 flex flex-col gap-3 rounded-lg bg-card p-4 shadow-card sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  총 <span className="font-medium text-foreground">{totalCount}</span>개
                </div>
                <div className="flex items-center justify-between gap-3 sm:justify-end">
                  <span className="text-sm text-muted-foreground">
                    페이지 <span className="font-medium text-foreground">{page}</span> / {totalPages}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                    >
                      이전
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages}
                    >
                      다음
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>

        {isModalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (e?.target === e?.currentTarget) {
                setSelectedPost(null);
              }
            }}
          >
            <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg bg-card shadow-lg">
              <div className="flex items-center justify-end border-b border-border px-4 py-3">
                <button
                  onClick={() => setSelectedPost(null)}
                  className="rounded-md p-2 text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
                  aria-label="닫기"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              <div className="max-h-[90vh] overflow-y-auto">
                <div className="bg-muted">
                  <div className="mx-auto w-full max-w-2xl">
                    <div className="aspect-[9/16] w-full overflow-hidden">
                      <Image
                        src={selectedPost?.image_url}
                        alt={'Before & After'}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                {selectedPost?.content && (
                  <div className="border-t border-border p-4">
                    <p className="whitespace-pre-line text-sm text-foreground">{selectedPost?.content}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <KakaoChatButton />
        <SiteFooter />
      </div>
    </>
  );
};

export default BeforeAfter;
